<script setup>
import { computed, useTemplateRef } from 'vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { scheduledSnoozeDateTime } from 'dashboard/helper/snoozeHelpers';
import SnoozeBadge from './SnoozeBadge.vue';
import CardAvatar from './CardAvatar.vue';
import CardContent from './CardContent.vue';
import CardLabels from './CardLabelsV5.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';
import InboxName from 'dashboard/components-next/Conversation/InboxName.vue';
import Avatar from 'next/avatar/Avatar.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import SLACardLabel from 'dashboard/components-next/Conversation/Sla/SLACardLabel.vue';
import CardStatusIcon from './CardStatusIcon.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  chat: { type: Object, required: true },
  currentContact: { type: Object, required: true },
  assignee: { type: Object, default: () => ({}) },
  inbox: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
  isActiveChat: { type: Boolean, default: false },
  isPinned: { type: Boolean, default: false },
  showAssignee: { type: Boolean, default: false },
  showInboxName: { type: Boolean, default: false },
  isInboxView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'selectConversation',
  'deSelectConversation',
  'click',
  'contextmenu',
]);

const lastMessageInChat = computed(() => getLastMessage(props.chat));
const showLabelsSection = computed(() => props.chat.labels?.length > 0);
const snoozedUntil = computed(
  () => props.chat?.snoozed_until || props.chat?.snoozedUntil
);
const scheduledAtLabel = computed(() =>
  scheduledSnoozeDateTime(snoozedUntil.value)
);

const voiceCallData = computed(() => {
  const last = lastMessageInChat.value;
  if (last?.content_type !== 'voice_call' || !last.call) {
    return { status: null, direction: null };
  }
  return {
    status: last.call.status,
    direction: last.call.direction === 'outgoing' ? 'outbound' : 'inbound',
  };
});

const unreadCount = computed(() => props.chat.unread_count);

const slaCardLabel = useTemplateRef('slaCardLabel');

const hasSlaPolicyId = computed(
  () =>
    !props.currentContact?.blocked &&
    (props.chat?.applied_sla?.id || slaCardLabel.value?.hasSlaThreshold)
);
const isAiActive = computed(
  () =>
    props.chat?.meta?.assignee_type === 'AgentBot' ||
    Boolean(props.chat?.assignee_agent_bot_id)
);
const aiActiveLabel = 'IA ativa';

const selectedModel = computed({
  get: () => props.selected,
  set: value => {
    if (value) {
      emit('selectConversation', value);
    } else {
      emit('deSelectConversation', value);
    }
  },
});
</script>

<template>
  <div
    class="conversation group relative mx-3 my-1 grid min-h-14 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 transition-[background-color,border-color,box-shadow] duration-150 hover:z-[1]"
    :aria-current="isActiveChat ? 'page' : undefined"
    :class="{
      'active z-[1] border-n-weak bg-n-solid-1 shadow-[0_3px_10px_rgba(15,23,42,0.08)]':
        isActiveChat,
      'selected border-n-brand/30 bg-n-alpha-2': selected && !isActiveChat,
      'border-transparent hover:border-n-weak hover:bg-n-alpha-1':
        !isActiveChat && !selected,
      'grid-cols-[auto_minmax(0,1.3fr)_minmax(0,2.2fr)_minmax(0,1.2fr)_auto]':
        showLabelsSection,
      'grid-cols-[auto_minmax(0,1.3fr)_minmax(0,3.4fr)_auto]':
        !showLabelsSection,
    }"
    @click="$emit('click', $event)"
    @contextmenu="$emit('contextmenu', $event)"
  >
    <div class="flex items-center justify-center" @click.stop>
      <Checkbox v-model="selectedModel" />
    </div>

    <!-- Contact -->
    <div class="flex min-w-0 items-center gap-2.5">
      <span
        class="shrink-0 rounded-full"
        :class="
          isActiveChat &&
          'ring-2 ring-n-brand/40 ring-offset-2 ring-offset-n-solid-1'
        "
      >
        <CardAvatar
          :contact="currentContact"
          :inbox="!isInboxView && showInboxName ? inbox : null"
          :selected="false"
          :enable-selection="false"
          :hide-thumbnail="false"
        />
      </span>
      <div class="min-w-0">
        <h4
          class="my-0 truncate text-sm font-semibold capitalize text-n-slate-12"
          :title="currentContact.name"
        >
          {{ currentContact.name }}
        </h4>
        <div class="flex min-w-0 items-center gap-1.5 text-xs text-n-slate-11">
          <span
            v-tooltip.top="{ content: chat.id, delay: { show: 500, hide: 0 } }"
            class="shrink-0 tabular-nums"
          >
            #{{ chat.id }}
          </span>
          <template v-if="!isInboxView && showInboxName">
            <span class="size-1 shrink-0 rounded-full bg-n-slate-7" />
            <InboxName
              :inbox="inbox"
              :show-icon="false"
              class="min-w-0 truncate"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Preview -->
    <div class="min-w-0">
      <CardContent
        :last-message="lastMessageInChat"
        :voice-call-status="voiceCallData.status"
        :voice-call-direction="voiceCallData.direction"
        :unread-count="unreadCount"
        :show-expanded-preview="false"
      />
    </div>

    <!-- Labels -->
    <div v-if="showLabelsSection" class="min-w-0">
      <CardLabels
        :labels="chat.labels"
        disable-toggle
        class="my-0 [&>div]:justify-end justify-end"
      />
    </div>

    <!-- Meta -->
    <div class="flex shrink-0 items-center justify-end gap-2">
      <div v-if="hasSlaPolicyId" class="shrink-0">
        <SLACardLabel ref="slaCardLabel" :chat="chat" />
      </div>
      <SnoozeBadge
        v-if="scheduledAtLabel"
        :snoozed-until="snoozedUntil"
        class="max-w-40"
      />
      <div
        class="flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-solid-2 px-1.5 py-1 dark:border-n-strong"
      >
        <span class="flex size-4 items-center justify-center">
          <CardPriorityIcon :priority="chat.priority" show-empty />
        </span>
        <span class="flex size-4 items-center justify-center">
          <CardStatusIcon :status="chat.status" show-empty />
        </span>
        <span class="flex size-4 items-center justify-center">
          <span
            v-if="isAiActive"
            v-tooltip.top="
              showAssignee && assignee.name
                ? `${assignee.name}: ${aiActiveLabel}`
                : aiActiveLabel
            "
            :aria-label="aiActiveLabel"
            role="img"
            class="inline-flex size-4 items-center justify-center text-n-teal-11"
          >
            <Icon icon="i-lucide-bot" class="size-3.5" />
          </span>
          <Avatar
            v-else-if="showAssignee && assignee.name"
            v-tooltip.top="{
              content: assignee.name,
              delay: { show: 500, hide: 0 },
            }"
            :name="assignee.name"
            :src="assignee.thumbnail"
            :size="16"
            :status="assignee.availability_status"
            hide-offline-status
          />
          <Icon
            v-else
            icon="i-woot-empty-assignee"
            class="size-4 text-n-slate-7"
          />
        </span>
      </div>
      <div class="flex w-[4.5rem] shrink-0 items-center justify-end gap-1">
        <span
          v-if="isPinned"
          v-tooltip.top="$t('CONVERSATION.CARD_CONTEXT_MENU.PINNED')"
          data-testid="pinned-conversation-badge"
          :aria-label="$t('CONVERSATION.CARD_CONTEXT_MENU.PINNED')"
          role="img"
          class="inline-flex shrink-0 items-center"
        >
          <Icon icon="i-hugeicons-pin-fill" class="size-3.5 !text-[#85858f]" />
        </span>
        <TimeAgo
          calendar
          :conversation-id="chat.id"
          :last-activity-timestamp="chat.timestamp"
          :created-at-timestamp="chat.created_at"
          class="font-440 !text-xs text-n-slate-11"
        />
      </div>
    </div>
  </div>
</template>
